import React, { useState, useEffect, useRef } from "react";
import { NotesBubble, parsePrefs } from './shared/PreorderNotes';
import axios from "axios";
import "./flashStyle.css";
import { toast } from "react-toastify";
import config from '../config';
import { bbqTerm } from '../utils/bbqTerms';
import { getPollMs } from '../utils/appSettings';
import { enqueuePrintJob, processPrintQueue } from "../utils/printQueue";
import { useBpFlags } from "../Responsive";
import { franc } from "franc-min";

import i18n from "../i18n";
const tr = i18n.t.bind(i18n);

const VOICEOVER_KEY = "estiasync_voiceover";
const ALARMS_KEY = "estiasync_alarms_on";
const DEFAULT_TTS_LANG = "en-US"

const pad2 = (value) => String(value).padStart(2, "0");

// Συνοδευτικά γεύματος: είτε από το core flow (ontheside state → side_completed)
// είτε από την παραγγελιοληψία (paired order_items → ordering_sides_*).
// hasSiders: υπάρχουν συνοδευτικά · sidersReady: ΟΛΑ έτοιμα (πράσινο check,
// ξεκλειδώνει Done/Served του κυρίως) · sidersPending: υπάρχουν και εκκρεμούν.
const sidersOf = (item) => {
  const orderingTotal = Number(item.ordering_sides_total || 0);
  const hasSiders = !!item.ontheside?.data?.[0] || orderingTotal > 0;
  const sidersReady = !!item.side_completed ||
    (orderingTotal > 0 && Number(item.ordering_sides_ready || 0) >= orderingTotal);
  return { hasSiders, sidersReady, sidersPending: hasSiders && !sidersReady, orderingTotal };
};

const getLocalDateInputValue = (date = new Date()) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const getNestedValue = (obj, path) =>
  String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((acc, key) => acc?.[key], obj);

const getFirstDefinedValue = (obj, paths = []) => {
  for (const path of paths) {
    const value = getNestedValue(obj, path);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const normalizeSearchValue = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const buildHistoryClientName = (row = {}) => {
  const lastname = getFirstDefinedValue(row, [
    "client_lastname",
    "lastname",
    "client.lastname",
  ]);
  const firstname = getFirstDefinedValue(row, [
    "client_firstname",
    "firstname",
    "client.firstname",
  ]);
  const combined = `${lastname} ${firstname}`.replace(/\s+/g, " ").trim();
  return combined || getFirstDefinedValue(row, ["client_name", "client.fullname"]);
};

const formatHistoryTime = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const timeMatch = raw.match(/(?:^|\s)(\d{2}:\d{2})(?::\d{2})?(?:$|\s)/);
  if (timeMatch?.[1]) return timeMatch[1];

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const BBQ = () => {

  const [schedule, setSchedule] = useState([]);
  const [error, setError] = useState("");
  const [buttonStates, setButtonStates] = useState({});
  const [timeleftData, setTimeleftData] = useState({});
  const [bbqs, setBBQs] = useState([]);
  const [colors, setColors] = useState({});
  const [details, setDetails] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
  const [audio] = useState(new Audio('/alarm.wav'));
  const [playing, setPlaying] = useState(false);

  const [alarmsMuted, setAlarmsMuted] = useState(
    () => localStorage.getItem(ALARMS_KEY) === "1"
  );
  
  // if alarms were ON previously, treat as "used" so notifications can play immediately
  const [alarmUsed, setAlarmUsed] = useState(
    () => localStorage.getItem(ALARMS_KEY) === "1"
  );  
  
  const [expandedByRes, setExpandedByRes] = useState({});
  const seenMeatIdsRef = useRef(new Set());

  // Συννεφάκια σημειώσεων (11/2026): ανοιχτά από προεπιλογή· ό,τι κλείσει ο χρήστης μένει κλειστό
  // σε αυτή τη συσκευή (localStorage — αν δεν είναι διαθέσιμο, απλώς δεν θυμάται).
  const NOTES_KEY = 'bbq_notes_collapsed_v1';
  const [notesCollapsed, setNotesCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NOTES_KEY) || '{}') || {}; } catch { return {}; }
  });
  const toggleNotes = (k) => setNotesCollapsed((prev) => {
    const next = { ...prev };
    if (next[k]) delete next[k]; else next[k] = 1;
    try { localStorage.setItem(NOTES_KEY, JSON.stringify(next)); } catch { /* χωρίς αποθήκευση */ }
    return next;
  });
  const { isTablet } = useBpFlags();
  const [voiceOverEnabled, setVoiceOverEnabled] = useState(
    () => localStorage.getItem(VOICEOVER_KEY) === "1"
  );

  const [selectedPrintMeats, setSelectedPrintMeats] = useState({});

  // "Pass" checkbox: stored on assignedmeats.pass, shared across devices via polling.
  const [savingPassMap, setSavingPassMap] = useState({});

  const togglePass = async (assignedmeatId, checked) => {
    const key = String(assignedmeatId ?? "");
    if (!key || savingPassMap[key]) return;

    const nextPass = checked ? 1 : 0;
    setSavingPassMap((prev) => ({ ...prev, [key]: true }));
    setSchedule((prev) => prev.map((row) => (
      String(row.assignedmeat) === key ? { ...row, pass: nextPass } : row
    )));

    try {
      await axios.put(
        tr('{{API_BASE_URL}}/assignedmeats/{{id}}/pass', {
          API_BASE_URL: config.API_BASE_URL,
          id: assignedmeatId,
        }),
        { pass: nextPass }
      );
    } catch (error) {
      console.error('Failed to update pass', error);
      toast.error(error?.response?.data?.error || tr('Failed to update pass.'));
      setSchedule((prev) => prev.map((row) => (
        String(row.assignedmeat) === key ? { ...row, pass: nextPass ? 0 : 1 } : row
      )));
    } finally {
      setSavingPassMap((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const [actualQtyDrafts, setActualQtyDrafts] = useState({});
  const [savingActualQtyMap, setSavingActualQtyMap] = useState({});

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyDate, setHistoryDate] = useState(() => getLocalDateInputValue());
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [historyReservationSearch, setHistoryReservationSearch] = useState("");
  const [historyClientSearch, setHistoryClientSearch] = useState("");
  const [historyMeatSearch, setHistoryMeatSearch] = useState("");
  const [historyBbqSearch, setHistoryBbqSearch] = useState("");

  const buttonStatesRef = useRef(buttonStates);
  useEffect(() => { buttonStatesRef.current = buttonStates; }, [buttonStates]);
  
  const alarmsMutedRef = useRef(alarmsMuted);
  useEffect(() => { alarmsMutedRef.current = alarmsMuted; }, [alarmsMuted]);
  
  const alarmUsedRef = useRef(alarmUsed);
  useEffect(() => { alarmUsedRef.current = alarmUsed; }, [alarmUsed]);
  
  const prevTimeleftRef = useRef({});
  const almostReadyNotifiedRef = useRef(new Set());
  
  useEffect(() => {
    prevTimeleftRef.current = timeleftData || {};
  }, [timeleftData]);
  
  const scheduleMapRef = useRef(new Map());
  
  useEffect(() => {
    const m = new Map();
    (schedule || []).forEach(it => m.set(String(it.assignedmeat), it));
    scheduleMapRef.current = m;
  }, [schedule]);  

  useEffect(() => {
    const validIds = new Set((schedule || []).map((it) => String(it.assignedmeat)));

    setSelectedPrintMeats((prev) => {
      let changed = false;
      const next = {};

      Object.entries(prev || {}).forEach(([id, enabled]) => {
        if (enabled && validIds.has(id)) next[id] = true;
        else if (enabled) changed = true;
      });

      if (!changed && Object.keys(next).length === Object.keys(prev || {}).length) return prev;
      return next;
    });
  }, [schedule]);
  
  const voiceOverEnabledRef = useRef(voiceOverEnabled);
  
  useEffect(() => {
    voiceOverEnabledRef.current = voiceOverEnabled;
  }, [voiceOverEnabled]);

  const speechQueueRef = useRef([]);
  const speechSpeakingRef = useRef(false);

  const clearSpeechQueue = () => {
    // clear buffered messages
    speechQueueRef.current = [];
    speechSpeakingRef.current = false;
  
    // stop anything currently speaking
    try {
      const synth = window.speechSynthesis;
      if (synth) synth.cancel();
    } catch {}
  };

  const toastOrigRef = useRef(null);
  const toastWrappedRef = useRef(false);
  
  const detailsLoadedRef = useRef(new Set());

  const extractToastText = (content) => {
    if (content == null) return "";
    if (typeof content === "function") return extractToastText(content());
    if (typeof content === "string" || typeof content === "number") return String(content);
    if (Array.isArray(content)) return content.map(extractToastText).join(" ");
    if (React.isValidElement(content)) return extractToastText(content.props?.children);
    return "";
  };
  
  const hasGreek = (s) => {
    for (const ch of String(s || "")) {
      const code = ch.codePointAt(0);
      if ((code >= 0x0370 && code <= 0x03FF) || (code >= 0x1F00 && code <= 0x1FFF)) return true;
    }
    return false;
  };
  
  const isGreekChar = (ch) => {
    const code = ch?.codePointAt?.(0);
    return (
      (code >= 0x0370 && code <= 0x03FF) ||
      (code >= 0x1F00 && code <= 0x1FFF)
    );
  };
  
  const isLatinChar = (ch) => /[A-Za-zA-OO-oo-y]/.test(ch);
  const isDigitChar = (ch) => /[0-9]/.test(ch);
  
  // Split a message into utterances so each utterance has ONE language.
  // - Greek letters => el-GR
  // - Digits => en-US (always)
  // - Latin letters => en-US (or pt-PT if you keep franc detection)
  const splitSpeechIntoUtterances = (text) => {
    const s = String(text || "");
    const raw = [];
    let buf = "";
    let type = null; // "el" | "latin" | "en" | null
  
    const flush = () => {
      if (!buf) return;
      raw.push({ text: buf, type: type || "en" });
      buf = "";
    };
  
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
  
      let t = null;
      if (isGreekChar(ch)) t = "el";
      else if (isDigitChar(ch)) t = "en";
      else if (isLatinChar(ch)) t = "latin";
      // punctuation/space: t stays null
  
      if (!t) {
        // keep punctuation/space with current segment (so speech sounds natural)
        buf += ch;
        continue;
      }
  
      if (type == null) type = t;
  
      if (t !== type) {
        flush();
        type = t;
      }
  
      buf += ch;
    }
    flush();
  
    // Map "type" to langTag and merge adjacent same-lang parts
    const parts = [];
    for (const p of raw) {
      let langTag;
      if (p.type === "el") langTag = "el-GR";
      else if (p.type === "latin") {
        // If you want Portuguese detection for latin runs, keep this:
        langTag = detectLangTagFromText(p.text, DEFAULT_TTS_LANG);
        // Otherwise force english:
        // langTag = DEFAULT_TTS_LANG;
      } else {
        langTag = DEFAULT_TTS_LANG; // numbers & everything else => English
      }
  
      // Skip pure whitespace segments (or append them to previous)
      if (!p.text.replace(/\s+/g, "")) {
        if (parts.length) parts[parts.length - 1].text += p.text;
        continue;
      }
  
      if (parts.length && parts[parts.length - 1].langTag === langTag) {
        parts[parts.length - 1].text += p.text;
      } else {
        parts.push({ text: p.text, langTag });
      }
    }
  
    return parts;
  };
  
  const detectLangTagFromText = (text, fallbackTag = "en-US") => {
    const s = String(text || "").trim();
    if (!s) return "en-US";
  
    // If it's basically numbers/symbols (no letters at all), speak in English
    const hasAnyLetter = /[A-Za-z\u0370-\u03FF\u1F00-\u1FFF]/.test(s);
    if (!hasAnyLetter) return "en-US";
  
    // Greek is easy and very reliable
    if (hasGreek(s)) return "el-GR";
  
    // For Latin languages (en/pt), use franc   needs enough letters
    const lettersOnly = s.replace(/[^A-Za-zA-OO-oo-y]+/g, " ").trim();
    if (lettersOnly.length < 12) return fallbackTag; // too short to detect safely
  
    const iso3 = franc(lettersOnly, { minLength: 12 }); // e.g. "eng", "por"
    if (iso3 === "por") return "pt-PT";
    if (iso3 === "eng") return "en-US";
  
    return fallbackTag;
  };
  
  const formatSpeech = (kind, rawText, opts) => {
    const msg = String(rawText || "").trim();
    if (!msg) return "";
  
    const autoClose = Number(opts?.autoClose ?? 0);
    const isAlert = (opts?.voiceAction === true) || (autoClose >= 10000);
  
    if (!isAlert) return msg;
  
    if (kind === "warn")  return `prepare ${msg}`;  // ? English
    if (kind === "error") return `start ${msg}`;    // ? English
    if (kind === "success") {
      const suffix = "is almost ready";             // ? English
      const lower = msg.toLowerCase();
      const sLower = suffix.toLowerCase();
      return lower.includes(sLower) ? msg : `${msg} ${suffix}`;
    }
  
    return msg;
  };

  const getLangTag = () => {
    const lang = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
    return lang.startsWith("el") ? "el-GR" : "en-US";
  };
  
  const pickVoice = (synth, langTag) => {
    try {
      const voices = typeof synth.getVoices === "function" ? synth.getVoices() : [];
      const base = langTag.split("-")[0].toLowerCase();
      return voices.find(v => String(v.lang || "").toLowerCase().startsWith(base)) || null;
    } catch {
      return null;
    }
  };

  const speakNextFromQueue = () => {
    if (!voiceOverEnabledRef.current) return;
    if (speechSpeakingRef.current) return;
  
    const synth = window.speechSynthesis;
    if (!synth || !window.SpeechSynthesisUtterance) return;
  
    const next = speechQueueRef.current.shift();
    if (!next) return;
  
    speechSpeakingRef.current = true; // ? ADD THIS
  
    const u = new SpeechSynthesisUtterance(next.text);
    u.lang = next.langTag;
  
    const v = pickVoice(synth, next.langTag);
    if (v) u.voice = v;
  
    u.onend = () => {
      speechSpeakingRef.current = false;
      speakNextFromQueue();
    };
  
    u.onerror = () => {
      speechSpeakingRef.current = false;
      speakNextFromQueue();
    };
  
    try { synth.resume?.(); } catch {}
    synth.speak(u);
  };
  
  const enqueueSpeech = (text) => {
    const msg = String(text || "");
    if (!msg.trim()) return;
  
    const parts = splitSpeechIntoUtterances(msg);
    parts.forEach(p => speechQueueRef.current.push(p));
  
    speakNextFromQueue();
  };

  const toggleVoiceOver = () => {
    const next = !voiceOverEnabled;
    
    if (!next) clearSpeechQueue();

    // If enabling but not supported, don t enable
    if (
      next &&
      (!window.speechSynthesis || !window.SpeechSynthesisUtterance)
    ) {
      toast.info(tr("VoiceOver is not supported in this browser."));
      return;
    }

    voiceOverEnabledRef.current = next;

    setVoiceOverEnabled(next);
    localStorage.setItem(VOICEOVER_KEY, next ? "1" : "0");

    if (next) {
      // user gesture (button click) helps iOS allow TTS
      enqueueSpeech(tr("VoiceOver enabled"));
    } else {
      try { } catch {}
    }
  };

  // Wrap toast.* while VoiceOver is ON (so ANY toast call is spoken)
  useEffect(() => {
    // restore if turning OFF
    if (!voiceOverEnabled) {
      if (toastWrappedRef.current && toastOrigRef.current) {
        const o = toastOrigRef.current;
        toast.success = o.success;
        toast.error   = o.error;
        toast.warn    = o.warn;
        toast.info    = o.info;
        if (o.dark) toast.dark = o.dark;
        toastWrappedRef.current = false;
        clearSpeechQueue();
      }
      return;
    }

    // already wrapped
    if (toastWrappedRef.current) return;

    toastOrigRef.current = {
      success: toast.success,
      error: toast.error,
      warn: toast.warn,
      info: toast.info,
      dark: toast.dark,
    };

    const wrap = (kind, fn) => (content, opts) => {
      const id = fn(content, opts);
    
      const raw = extractToastText(content);
      const spoken = formatSpeech(kind, raw, opts);
    
      enqueueSpeech(spoken);
      return id;
    };

    toast.success = wrap("success", toastOrigRef.current.success);
    toast.error   = wrap("error",   toastOrigRef.current.error);
    toast.warn    = wrap("warn",    toastOrigRef.current.warn);
    toast.info    = wrap("info",    toastOrigRef.current.info);
    if (toastOrigRef.current.dark) toast.dark = wrap("dark", toastOrigRef.current.dark);

    toastWrappedRef.current = true;

    return () => {
      // cleanup on unmount
      if (toastWrappedRef.current && toastOrigRef.current) {
        const o = toastOrigRef.current;
        toast.success = o.success;
        toast.error   = o.error;
        toast.warn    = o.warn;
        toast.info    = o.info;
        if (o.dark) toast.dark = o.dark;
        toastWrappedRef.current = false;
      }
    };
  }, [voiceOverEnabled]);


  // Group items by reservation (keep first-seen order)
  const groups = React.useMemo(() => {
    const order = [];
    const map = {};
    for (const it of schedule) {
      const id = it.reservation_id;
      if (!map[id]) {
        map[id] = [];
        order.push(id);
      }
      map[id].push(it);
    }
    return { order, map };
  }, [schedule]);

  // Running / should start helpers
  const isRunning = (it) =>
    ['restart', 'run', 'stop'].includes(buttonStates[it.assignedmeat]) ||
    (timeleftData[it.assignedmeat] != null && timeleftData[it.assignedmeat] > 0);

  const shouldHaveStarted = (it) => calculateTimeDifference(it.start_time) <= 0;

  // Initialize default expansion (once per reservation id)
  useEffect(() => {
    if (!schedule.length) return;
    setExpandedByRes(prev => {
      const next = { ...prev };
      for (const resId of groups.order) {
        if (next[resId] === undefined) {
          const items = groups.map[resId];
          // expand by default if any item has started / is running
          next[resId] = items.some(it => isRunning(it) || shouldHaveStarted(it));
        }
      }
      return next;
    });
  }, [schedule, groups, buttonStates, timeleftData]);

  const rowAnimatedStyle = (visible) => ({
    height: visible ? 37 : 0,
    overflow: 'hidden',
    transition: 'height 260ms ease, opacity 200ms ease',
    opacity: visible ? 1 : 0,
  });

  const formatArrivalHHMM = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
  
    const hhmmMatch = raw.match(/(\d{2}):(\d{2})/);
    if (hhmmMatch) return `${hhmmMatch[1]}:${hhmmMatch[2]}`;
  
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  
  const buildStickerPayloadFromReservationItems = (items = []) => {
    const first = items[0] || {};
    const resId = first.reservation_id;
  
    const tables =
      details?.[resId]?.tables ||
      details?.[resId]?.reservation?.tables ||
      first.tables ||
      '';
  
    const lastname =
      details?.[resId]?.client?.lastname ??
      details?.[resId]?.client_lastname ??
      '';
  
    const firstname =
      details?.[resId]?.client?.firstname ??
      details?.[resId]?.client_firstname ??
      '';
  
    const fullName = tr('{{lastname}} {{firstname}}', {
      lastname,
      firstname
    }).trim();
  
    const arrivalRaw =
      first.reservation_arrival_time ||
      details?.[resId]?.arrival ||
      details?.[resId]?.reservation?.arrival ||
      '';
  
    return {
      reservationId: resId ?? '',
      arrivalTime: formatArrivalHHMM(arrivalRaw),
      tables: String(tables || '').trim(),
      fullName,
      meats: items.map((item) => ({
        meatId: item?.assignedmeat ?? item?.meat ?? '',
        requestedQuantity: item?.grams ?? item?.quantity ?? item?.qty ?? '',
        actualQuantity: getEffectiveActualQtyForItem(item),
        cookType: item?.cooktype_name ?? item?.cooktype ?? '',
        meatName: item?.meat_name ?? item?.meat ?? ''
      }))
    };
  };

  const getActualQtyInputValue = (item) => {
    const key = String(item?.assignedmeat ?? '');
    if (Object.prototype.hasOwnProperty.call(actualQtyDrafts, key)) {
      return actualQtyDrafts[key];
    }
    return String(item?.actual_quantity ?? item?.grams ?? '');
  };

  const getEffectiveActualQtyForItem = (item) => {
    const draft = actualQtyDrafts[String(item?.assignedmeat ?? '')];
    const parsedDraft = Number(String(draft ?? '').trim());
    if (Number.isInteger(parsedDraft) && parsedDraft > 0) {
      return parsedDraft;
    }
    return item?.actual_quantity ?? item?.actualQuantity ?? item?.grams ?? item?.quantity ?? item?.qty ?? '';
  };

  const handleActualQtyDraftChange = (assignedmeatId, value) => {
    const key = String(assignedmeatId ?? '');
    setActualQtyDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const saveActualQuantity = async (item, rawValue = null) => {
    const key = String(item?.assignedmeat ?? '');
    if (!key) return;

    const nextRaw = String(rawValue ?? getActualQtyInputValue(item)).trim();
    const currentValue = String(item?.actual_quantity ?? item?.grams ?? '').trim();

    if (!nextRaw) {
      setActualQtyDrafts((prev) => ({ ...prev, [key]: currentValue }));
      toast.error(tr('Actual quantity is required.'));
      return;
    }

    const parsed = Number(nextRaw);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      setActualQtyDrafts((prev) => ({ ...prev, [key]: currentValue }));
      toast.error(tr('Actual quantity must be a positive integer.'));
      return;
    }

    if (String(parsed) === currentValue) {
      setActualQtyDrafts((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, key)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }

    try {
      setSavingActualQtyMap((prev) => ({ ...prev, [key]: true }));
      await axios.put(
        tr('{{API_BASE_URL}}/assignedmeats/{{id}}/actual-quantity', {
          API_BASE_URL: config.API_BASE_URL,
          id: item.assignedmeat,
        }),
        { actual_quantity: parsed }
      );

      setSchedule((prev) => prev.map((row) => (
        String(row.assignedmeat) === key
          ? { ...row, actual_quantity: parsed }
          : row
      )));

      setActualQtyDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (error) {
      console.error('Failed to update actual quantity', error);
      toast.error(error?.response?.data?.error || tr('Failed to update actual quantity.'));
    } finally {
      setSavingActualQtyMap((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };
  
  // Shared print state from assignedmeats.print_status (synced via schedule polling):
  // 0 = not printed, 1 = queued, 2 = printed, 3 = failed
  const setLocalPrintStatus = (assignedMeatIds = [], status = 1) => {
    const ids = new Set((assignedMeatIds || []).map((id) => String(id)).filter(Boolean));
    if (!ids.size) return;
    setSchedule((prev) => prev.map((row) => (
      ids.has(String(row.assignedmeat)) ? { ...row, print_status: status } : row
    )));
  };

  const clearSelectedPrintItems = (assignedMeatIds = []) => {
    const ids = new Set((assignedMeatIds || []).map((id) => String(id)).filter(Boolean));
    if (!ids.size) return;

    setSelectedPrintMeats((prev) => {
      const next = { ...(prev || {}) };
      let changed = false;
      ids.forEach((id) => {
        if (next[id]) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const togglePrintSelection = (assignedmeatId, checked) => {
    const key = String(assignedmeatId ?? '');
    if (!key) return;

    setSelectedPrintMeats((prev) => {
      const next = { ...(prev || {}) };
      if (checked) next[key] = true;
      else delete next[key];
      return next;
    });
  };

  const getSelectedReservationItems = (reservationId) => {
    const items = groups.map?.[reservationId] || [];
    return items.filter((it) => selectedPrintMeats[String(it.assignedmeat)]);
  };

  const getPrintStatusTone = (item) => {
    const status = Number(item?.print_status || 0);
    if (status === 2) return 'green';   // printed successfully
    if (status === 1) return 'yellow';  // queued, waiting for the print station
    if (status === 3) return 'orange';  // print failed
    return 'red';                       // not printed yet
  };

  const PRINT_TONE_COLORS = {
    green:  { bg: 'rgba(25, 135, 84, 0.16)',  ring: 'rgba(25, 135, 84, 0.22)',  fg: 'rgba(25, 110, 66, 0.95)' },
    yellow: { bg: 'rgba(255, 193, 7, 0.22)',  ring: 'rgba(255, 193, 7, 0.35)',  fg: 'rgba(150, 110, 0, 0.95)' },
    orange: { bg: 'rgba(253, 126, 20, 0.20)', ring: 'rgba(253, 126, 20, 0.35)', fg: 'rgba(180, 80, 10, 0.95)' },
    red:    { bg: 'rgba(220, 53, 69, 0.15)',  ring: 'rgba(220, 53, 69, 0.22)',  fg: 'rgba(152, 27, 27, 0.95)' },
  };

  const getPrintControlWrapStyle = (tone, includeCheckbox = true) => {
    const colors = PRINT_TONE_COLORS[tone];
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: includeCheckbox ? 6 : 0,
      padding: includeCheckbox ? '2px 6px 2px 5px' : '2px 5px',
      borderRadius: 8,
      background: colors ? colors.bg : 'transparent',
      boxShadow: colors ? `inset 0 0 0 1px ${colors.ring}` : 'none',
      transition: 'background 180ms ease, box-shadow 180ms ease',
    };
  };

  const getPrintButtonStyle = (tone) => ({
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: 1,
    padding: '2px 4px',
    color: PRINT_TONE_COLORS[tone]?.fg || 'inherit',
  });

  const sendStickerPrint = async (items, { singleToast = false } = {}) => {
    try {
      const list = Array.isArray(items) ? items.filter(Boolean) : [];
      if (!list.length) return;

      const payload = buildStickerPayloadFromReservationItems(list);
      const meatIds = list.map((it) => it?.assignedmeat).filter(Boolean);

      localStorage.setItem('labelPrinterHint', 'ZD220');
      localStorage.setItem('labelOrientation', 'N');

      await enqueuePrintJob({
        payload,
        meatIds,
        reservation: list[0]?.reservation_id || null,
      });

      setLocalPrintStatus(meatIds, 1); // queued -> yellow (server sets it too)
      clearSelectedPrintItems(meatIds);

      if (singleToast && list.length === 1) {
        const item = list[0];
        toast.success(tr('Sticker sent: #{{var1}} ({{meat_name}})', {
          var1: ('  ' + (item?.assignedmeat ?? '')).slice(-2),
          meat_name: item?.meat_name || ''
        }), { autoClose: 3000 });
      } else {
        toast.success(tr('Stickers sent: {{n}}', { n: list.length }), { autoClose: 3000 });
      }

      // If this very device has QZ Tray, print immediately instead of
      // waiting for the next poll tick.
      processPrintQueue({ onJobPrinted: () => fetchSchedule() });
    } catch (e) {
      console.error('Sticker queue failed', e);
      toast.error(tr('Failed to queue the sticker for printing.'));
    }
  };

  const handlePrintStickerGroup = async (items) => {
    await sendStickerPrint(items, { singleToast: false });
  };

  const handlePrintSticker = async (item) => {
    const clickedId = String(item?.assignedmeat ?? '');
    const selectedInReservation = getSelectedReservationItems(item?.reservation_id);
    const useSelected = Boolean(selectedPrintMeats[clickedId]) && selectedInReservation.length > 1;
    const list = useSelected ? selectedInReservation : [item];

    await sendStickerPrint(list, { singleToast: list.length === 1 });
  };

  const getRowPrinterTitle = (item) => {
    const clickedId = String(item?.assignedmeat ?? '');
    const selectedInReservation = getSelectedReservationItems(item?.reservation_id);
    const useSelected = Boolean(selectedPrintMeats[clickedId]) && selectedInReservation.length > 1;
    return useSelected ? bbqTerm('Print selected meats') : tr('Print sticker');
  };

  const toggle = () => {
    const nextOn = !alarmsMuted;
  
    setAlarmUsed(true);
    setAlarmsMuted(nextOn);
    localStorage.setItem(ALARMS_KEY, nextOn ? "1" : "0");
  
    if (nextOn) setPlaying(true);
    else setPlaying(false);
  };

  useEffect(() => {
      playing ? audio.play() : audio.pause();
    },
    [playing]
  );

  useEffect(() => {
    audio.addEventListener('ended', () => setPlaying(false));
    return () => {
      audio.removeEventListener('ended', () => setPlaying(false));
    };
  }, []);

  useEffect(() => {
      if(!error)
        setTimeout(()=>{
          setError('');
        }, 5000);
    },
    [error]
  );

  useEffect(() => {  
    var notify = false;
    schedule.filter((item) => !timeleftData[item.assignedmeat]).forEach((item)=>{
      var diff2start = calculateTimeDifference(item.start_time);
      if(diff2start === 5)
        toast.warn(('  '+item.assignedmeat).slice(-2)+') '+item.meat_name+' '+item.grams+' '+item.cooktype_name, { autoClose: 15000 });
      if(diff2start === 1)
        toast.error(('  '+item.assignedmeat).slice(-2)+') '+item.meat_name+' '+item.grams+' '+item.cooktype_name, { autoClose: 15000 });
      notify = (notify || (diff2start === 1) || (diff2start === 5));
    });
    if(alarmUsed && alarmsMuted && notify)
      setPlaying(true);
  }, [currentTime, alarmUsed, alarmsMuted]);

  // Update current time 
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  const calculateTimeDifference = (startTime) => {
    const now = currentTime;
    const start = new Date(tr('{{var1}} {{startTime}}', {
      var1: now.toDateString(),
      startTime
    }));
    const minutes = Math.floor((start - now) / 60000); // Return difference in minutes
    //alarm = (minutes==5 || minutes==1);
    return minutes;
  };

  const getBarStyle = (timeDiff) => {
    const backgroundColor = timeDiff > 60 ? "gray" : (timeDiff <= 10 ? "darkRed" : "darkGreen");
    return {
      width: tr('{{var1}}%', {
        var1: timeDiff>100?100:(timeDiff<15?15:timeDiff)
      }),
      backgroundColor,
      height: "9px",    
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "8px",
      color: "white"
    };
  };

  const getRandomColor = (reservationLastDigit) => {
    var letters = '0123456789ABCDEF';
    var color = '#';
    for (var i = 0; i < 3; i++) {
      color += letters[Math.floor(Math.random() * 16)];
      color += letters[13-reservationLastDigit];
    }
    return color;
  };

  const toSoftGroupBorderColor = (value, alpha = 0.3) => {
    const raw = String(value || '').trim();
    if (!raw) return `rgba(15, 76, 129, ${alpha})`;

    if (raw.startsWith('rgba(') || raw.startsWith('rgb(')) {
      return raw.replace(/rgba?\(([^)]+)\)/, (match, inner) => {
        const parts = inner.split(',').map((part) => part.trim());
        if (parts.length < 3) return match;
        return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
      });
    }

    let hex = raw.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map((ch) => ch + ch).join('');
    }

    if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
      return `rgba(15, 76, 129, ${alpha})`;
    }

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  

  const fetchSchedule = () => {
    axios
      .get(tr('{{API_BASE_URL}}/assigned-meats-schedule-today', {
        API_BASE_URL: config.API_BASE_URL
      }))
      .then((response) => {
        const data = response.data || [];
  
        // keep colors updated (and persist missing ones once)
        setColors(prev => {
          const next = { ...prev };
  
          data.forEach((it) => {
            const reservation = it.reservation_id;
            if (!reservation) return;
  
            if (it.color) {
              next[reservation] = it.color;
              return;
            }
  
            // only generate once per reservation (avoid spamming update-color)
            if (!next[reservation]) {
              const randomColor = getRandomColor(+('' + reservation).slice(-1));
              next[reservation] = randomColor;
  
              axios.post(
                tr('{{API_BASE_URL}}/reservations/update-color', { API_BASE_URL: config.API_BASE_URL }),
                { reservation_id: reservation, color: randomColor }
              ).catch((error) => {
                console.error('Failed to save color for reservation', reservation, error.message);
              });
            }
          });
  
          return next;
        });
  
        // update schedule (this contains side_completed!)
        setSchedule(data);

        // Νέα κρέατα στον πίνακα (π.χ. μόλις δημιουργήθηκε κράτηση με προπαραγγελία):
        // φέρε αμέσως current-state + timeleft, αλλιώς η γραμμή έμενε «μισή» έως 60".
        const ids = new Set(data.map((x) => Number(x.assignedmeat)).filter(Boolean));
        const hasNew = [...ids].some((id) => !seenMeatIdsRef.current.has(id));
        seenMeatIdsRef.current = ids;
        if (hasNew) { setCurrentTime(new Date()); update(); }
  
        // fetch reservation details only once per reservation
        const uniqueResIds = Array.from(new Set(data.map(x => Number(x.reservation_id)).filter(Boolean)));
        uniqueResIds.forEach((reservation) => {
          if (detailsLoadedRef.current.has(reservation)) return;
          detailsLoadedRef.current.add(reservation);
  
          axios
            .get(tr('{{API_BASE_URL}}/reservation-details/{{reservation}}', {
              API_BASE_URL: config.API_BASE_URL,
              reservation
            }))
            .then((r) => {
              setDetails(prev => ({ ...prev, [reservation]: r.data }));
            })
            .catch((error) => {
              console.error('Failed to retrieve details for reservation', reservation, error.message);
            });
        });
      })
      .catch((error) => {
        setError("Failed to load the schedule.");
        console.error(error);
      });
  };
  

  useEffect(() => {
    fetchSchedule();                 // initial load
    const interval = setInterval(fetchSchedule, getPollMs('normal'));
    return () => clearInterval(interval);
  }, []);

  // Print station: always on. Devices with a local QZ Tray automatically
  // pick up queued jobs and print them; all other devices are no-ops.
  useEffect(() => {
    const interval = setInterval(() => {
      processPrintQueue({
        onJobPrinted: () => fetchSchedule(),
        onJobFailed: () => {
          toast.error(tr('Printing failed. Is QZ Tray running and authorized?'));
          fetchSchedule();
        },
      });
    }, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  

  // Load schedule and current states on page load
  useEffect(() => {
    Promise.all([
      axios.get(tr('{{API_BASE_URL}}/synchronizations/current-state', {
        API_BASE_URL: config.API_BASE_URL
      })),
      axios.get(tr('{{API_BASE_URL}}/bbqs', {
        API_BASE_URL: config.API_BASE_URL
      })),
    ])
      .then(([syncsResponse, bbqsResponse]) => {
        const currentStates = syncsResponse.data;
        setBBQs(bbqsResponse.data);
  
        const initialStates = {};
        const initialTimeleft = {};
        let notify = false;
  
        currentStates.forEach((item) => {
          initialStates[item.assignedmeat] = item.action;
          initialTimeleft[item.assignedmeat] = item.timeleft;
          if (+item.timeleft === 1) {
            // ...
            notify = true;
          }
        });
  
        if (alarmUsed && alarmsMuted && notify) setPlaying(true);
        setButtonStates(initialStates);
        setTimeleftData(initialTimeleft);
      })
      .catch((error) => {
        setError("Failed to load current states or BBQs.");
        console.error(error);
      });
  }, [currentTime]);

  const handleBBQChange = (assignedmeatId, bbqId) => {
    axios
      .post(tr('{{API_BASE_URL}}/synchronizations/update-bbq', {
      API_BASE_URL: config.API_BASE_URL
    }), {
        assignedmeat: assignedmeatId,
        bbq: bbqId,
      })
      .then(() => {
        setSchedule((prev) =>
          prev.map((item) =>
            item.assignedmeat === assignedmeatId
              ? { ...item, bbq: bbqId } // Update BBQ ID for the record
              : item
          )
        );
      })
      .catch((error) => {
        setError("Failed to update BBQ.");
        console.error(error);
      });
  };


  const update = () => {
    axios
      .get(tr('{{API_BASE_URL}}/synchronizations/timeleft', {
        API_BASE_URL: config.API_BASE_URL
      }))
      .then((response) => {
        const updatedTimeleft = {};
        const newlyAlmost = []; // ids that just entered <= 1'
  
        (response.data || []).forEach((row) => {
          const id = String(row.assignedmeat);
          const it = scheduleMapRef.current.get(id);
          if (!it) {
            // not in today's visible schedule => ignore completely
            almostReadyNotifiedRef.current.delete(id);
            return;
          }
          const tl = Number(row.timeleft);
  
          updatedTimeleft[id] = tl;
  
          const isDone = buttonStatesRef.current[id] === "done";
  
          // Clear notification if done or back to >1 (or invalid)
          if (isDone || !Number.isFinite(tl) || tl > 1) {
            almostReadyNotifiedRef.current.delete(id);
            return;
          }
  
          // tl <= 1 : fire only once per entry
          if (!almostReadyNotifiedRef.current.has(id)) {
            almostReadyNotifiedRef.current.add(id);
            newlyAlmost.push(id);
          }
        });
  
        // ? must be inside .then (so it can use updatedTimeleft / newlyAlmost)
        setTimeleftData(updatedTimeleft);
  
        if (newlyAlmost.length) {
          newlyAlmost.forEach((id) => {
            const it = scheduleMapRef.current.get(String(id));
            const label =
              it
                ? `${('  ' + id).slice(-2)}) ${it.meat_name} ${it.grams} ${it.cooktype_name}`
                : `${('  ' + id).slice(-2)})`;
  
            toast.success(label, {
              autoClose: 15000,
              voiceAction: true, // keep this so VoiceOver adds  is almost ready 
            });
          });
  
          if (alarmUsedRef.current && alarmsMutedRef.current) setPlaying(true);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch timeleft:", error.message);
      });
  };


  // Periodically fetch timeleft updates
  useEffect(() => {
    update(); // run once immediately
    const interval = setInterval(update, 15000); // or 10000 if you want it snappier
    return () => clearInterval(interval);
  }, []);

  const handleStart = (id, minutes) => {
    axios
      .post(tr('{{API_BASE_URL}}/synchronizations/start', {
      API_BASE_URL: config.API_BASE_URL
    }), {
        assignedmeat: id,
        minutes: minutes,
      })
    .then((response) => {
      // update the button state
      setButtonStates((prev) => ({
        ...prev,
        [id]: "restart",
      }));

      // update the time left
      setTimeleftData((prev) => ({
        ...prev,
        [id]: minutes,
      }));

      setCurrentTime(new Date());
      })
      .catch((error) => {
        setError("Failed to start synchronization.");
        console.error(error);
      });
  };

  const handleRestart = (id, minutes) => {
    axios
      .post(tr('{{API_BASE_URL}}/synchronizations/restart', {
      API_BASE_URL: config.API_BASE_URL
    }), {
        assignedmeat: id,
        minutes: minutes,
      })
    .then((response) => {
      // update the button state
      setButtonStates((prev) => ({
        ...prev,
        [id]: "restart",
      }));

      // update the time left
      setTimeleftData((prev) => ({
        ...prev,
        [id]: minutes,
      }));

      setCurrentTime(new Date());
      })
      .catch((error) => {
        setError("Failed to restart synchronization.");
        console.error(error);
      });
  };

  const handleRetime = (id, retime) => {
    axios
      .post(tr('{{API_BASE_URL}}/synchronizations/retime', {
      API_BASE_URL: config.API_BASE_URL
    }), {
        assignedmeat: id,
        retime: retime,
      })
      .then(() => {
        setCurrentTime(new Date());
      })
      .catch((error) => {
      setError(tr('Failed to retime {{id}}.', {
        id
      }));
      console.error(error);
    });
  };

  const handleToggle = (id, currentAction) => {
    axios
      .post(tr('{{API_BASE_URL}}/synchronizations/toggle', {
      API_BASE_URL: config.API_BASE_URL
    }), {
        assignedmeat: id,
        action: currentAction,
      })
      .then(() => {
        setButtonStates((prev) => ({
          ...prev,
          [id]: currentAction,
        }));
        update();
      })
      .catch((error) => {
      setError(tr('Failed to toggle action to {{currentAction}}.', {
        currentAction
      }));
      console.error(error);
    });
  };

  const handleDone = (id) => {
    axios
      .post(tr('{{API_BASE_URL}}/synchronizations/done', {
      API_BASE_URL: config.API_BASE_URL
    }), {
        assignedmeat: id,
      })
      .then(() => {
        setButtonStates((prev) => ({
          ...prev,
          [id]: "done",
        }));
        setTimeleftData((prev) => ({
          ...prev,
          [id]: 0,
        }));
        update();
      })
      .catch((error) => {
        setError("Failed to mark synchronization as done.");
        console.error(error);
      });
  };

  const handleDoneToggle = (id, isChecked) => {
    const url = isChecked
      ? tr('{{API_BASE_URL}}/synchronizations/done', {
      API_BASE_URL: config.API_BASE_URL
    })
      : tr('{{API_BASE_URL}}/synchronizations/undone', {
      API_BASE_URL: config.API_BASE_URL
    });

    axios
      .post(url, { assignedmeat: id })
      .then(() => {
        setButtonStates((prev) => ({
          ...prev,
          [id]: isChecked ? "done" : "stop",
        }));
  
        // When we mark as done, force timeleft to 0
        if (isChecked) {
          setTimeleftData((prev) => ({
            ...prev,
            [id]: 0,
          }));
        }
  
        // Refresh timeleft from server
        update();
      })
      .catch((error) => {
        setError(
          isChecked
            ? "Failed to mark synchronization as done."
            : "Failed to unset done state."
        );
        console.error(error);
      });
  };

  const handleServed = (id) => {
    axios
      .delete(tr('{{API_BASE_URL}}/assignedmeats/serve/{{id}}', {
      API_BASE_URL: config.API_BASE_URL,
      id
    }))
      .then(() => {
        setError("");
        setSchedule((prev) => prev.filter((item) => item.assignedmeat !== id));
        if (historyOpen) {
          fetchHistory(historyDate);
        }
      })
      .catch((error) => {
        const apiMessage = error?.response?.data?.error || error?.response?.data?.message;
        setError(apiMessage || bbqTerm('Failed to mark assigned meat as served.'));
        console.error(error);
      });
  };

  const handleForceOnTheSide = (reservationId) => {
    const ok = window.confirm(
      tr('Send OnTheSide action to kitchen for reservation {{reservationId}} ?', {
        reservationId
      })
    );
    if (!ok) return;

    axios
      .post(tr('{{API_BASE_URL}}/assignedstates/{{reservationId}}', {
      API_BASE_URL: config.API_BASE_URL,
      reservationId
    }), {
        state: 9, // ontheside state ID (same as in ontheside.js)
      })
      .then((response) => {
      toast.success(
        tr('OnTheSide action sent for reservation {{reservationId}}', {
          reservationId
        }),
        { autoClose: 3000 }
      );
    })
      .catch((error) => {
        console.error("Failed to send OnTheSide action:", error);
        toast.error("Failed to send OnTheSide action.");
      });
  };

  const fetchHistory = async (dateValue = historyDate) => {
    setHistoryLoading(true);
    setHistoryError("");

    try {
      const response = await axios.get(
        tr('{{API_BASE_URL}}/assigned-meats-history', {
          API_BASE_URL: config.API_BASE_URL
        }),
        {
          params: {
            date: dateValue,
          },
        }
      );

      const rows = Array.isArray(response.data) ? response.data : [];
      rows.sort((a, b) => {
        const aTime = new Date(
          getFirstDefinedValue(a, ["served_at", "served_time", "updated_at", "created_at"])
        ).getTime() || 0;
        const bTime = new Date(
          getFirstDefinedValue(b, ["served_at", "served_time", "updated_at", "created_at"])
        ).getTime() || 0;
        return bTime - aTime;
      });

      setHistoryRows(rows);
    } catch (err) {
      console.error("Failed to load served meats history.", err);
      setHistoryRows([]);
      setHistoryError(bbqTerm("Failed to load served meats history."));
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!historyOpen) return;
    fetchHistory(historyDate);
  }, [historyOpen, historyDate]);

  useEffect(() => {
    if (!historyOpen) return undefined;

    const handleEsc = (event) => {
      if (event.key === "Escape") setHistoryOpen(false);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEsc);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEsc);
    };
  }, [historyOpen]);

  const filteredHistoryRows = React.useMemo(() => {
    const generalNeedle = normalizeSearchValue(historySearch);
    const reservationNeedle = normalizeSearchValue(historyReservationSearch);
    const clientNeedle = normalizeSearchValue(historyClientSearch);
    const meatNeedle = normalizeSearchValue(historyMeatSearch);
    const bbqNeedle = normalizeSearchValue(historyBbqSearch);

    return historyRows.filter((row) => {
      const reservationValue = normalizeSearchValue([
        getFirstDefinedValue(row, ["reservation_id", "reservation.id"]),
        getFirstDefinedValue(row, ["tables", "reservation_tables", "reservation.tables"]),
      ].join(" "));

      const clientValue = normalizeSearchValue(buildHistoryClientName(row));

      const meatValue = normalizeSearchValue([
        getFirstDefinedValue(row, ["assignedmeat", "id"]),
        getFirstDefinedValue(row, ["meat_name", "meat.name", "meat"]),
        getFirstDefinedValue(row, ["grams", "quantity"]),
        getFirstDefinedValue(row, ["actual_quantity", "actualQuantity"]),
        getFirstDefinedValue(row, ["cooktype_name", "cooktype.name", "cooktype"]),
      ].join(" "));

      const bbqValue = normalizeSearchValue([
        getFirstDefinedValue(row, ["bbq_name", "bbq.name", "bbq"]),
        getFirstDefinedValue(row, ["order"]),
      ].join(" "));

      const generalValue = normalizeSearchValue([
        getFirstDefinedValue(row, ["served_at", "served_time", "updated_at", "created_at"]),
        reservationValue,
        clientValue,
        meatValue,
        bbqValue,
        getFirstDefinedValue(row, ["arrival", "reservation_arrival_time"]),
        getFirstDefinedValue(row, ["start_time"]),
        getFirstDefinedValue(row, ["ready_time"]),
      ].join(" "));

      return (
        (!generalNeedle || generalValue.includes(generalNeedle)) &&
        (!reservationNeedle || reservationValue.includes(reservationNeedle)) &&
        (!clientNeedle || clientValue.includes(clientNeedle)) &&
        (!meatNeedle || meatValue.includes(meatNeedle)) &&
        (!bbqNeedle || bbqValue.includes(bbqNeedle))
      );
    });
  }, [
    historyRows,
    historySearch,
    historyReservationSearch,
    historyClientSearch,
    historyMeatSearch,
    historyBbqSearch,
  ]);

  const resetHistoryFilters = () => {
    setHistorySearch("");
    setHistoryReservationSearch("");
    setHistoryClientSearch("");
    setHistoryMeatSearch("");
    setHistoryBbqSearch("");
  };

  const utilityButtonStyle = {
    color: "white",
    border: "1px solid rgba(255,255,255,0.16)",
    borderRadius: 10,
    padding: "6px 10px",
    fontWeight: 700,
    fontSize: 13,
    lineHeight: 1.1,
    boxShadow: "0 7px 14px rgba(0,0,0,0.10)",
  };

  const headerClockStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 14px",
    borderRadius: 999,
    color: "darkBlue",
    background: "linear-gradient(180deg, #f4f8ff 0%, #e5eefb 100%)",
    border: "1px solid #d5e1f0",
    textShadow: "2px 1px 3px rgba(128, 128, 128, 0.55)",
    boxShadow: "0 8px 18px rgba(18, 60, 96, 0.08)",
    whiteSpace: "nowrap",
    fontSize: "larger",
  };

  const historyLaunchButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid #0f4c81",
    background: "linear-gradient(180deg, #1d71b8 0%, #0f4c81 100%)",
    color: "white",
    borderRadius: 999,
    padding: "7px 14px",
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: 0.2,
    boxShadow: "0 10px 22px rgba(15, 76, 129, 0.24)",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const historyFilterCardStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 0,
    padding: "10px 12px",
    borderRadius: 12,
    background: "rgba(255,255,255,0.78)",
    border: "1px solid #d7e4f3",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
  };

  const historyFieldLabelStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: "#36506b",
    letterSpacing: 0.15,
  };

  const historyInputStyle = {
    width: "100%",
    height: 38,
    border: "1px solid #b8cbe0",
    borderRadius: 10,
    padding: "0 12px",
    fontSize: 14,
    color: "#132f4c",
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const historyActionButtonBaseStyle = {
    height: 38,
    borderRadius: 10,
    padding: "0 14px",
    fontWeight: 700,
    cursor: "pointer",
    border: "1px solid #b8cbe0",
    boxShadow: "0 8px 18px rgba(18, 60, 96, 0.08)",
  };

  const historyPrimaryButtonStyle = {
    ...historyActionButtonBaseStyle,
    color: "white",
    background: "linear-gradient(180deg, #1d71b8 0%, #0f4c81 100%)",
    border: "1px solid #0f4c81",
  };

  const historySecondaryButtonStyle = {
    ...historyActionButtonBaseStyle,
    color: "#163a5f",
    background: "white",
  };

  const historyCloseButtonStyle = {
    ...historyActionButtonBaseStyle,
    color: "white",
    background: "#66788b",
    border: "1px solid #66788b",
  };

  return (
    <div className={isTablet ? 'tablet-scroll' : ''} style={{ width: '95%', margin: 'auto' }}>
      {error ? (
        <div
          style={{
            margin: '10px 0 14px',
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid #f1b0b7',
            background: '#fff5f6',
            color: '#9b1c28',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError('')}
            style={{
              border: '1px solid #e39aa3',
              background: 'white',
              color: '#9b1c28',
              borderRadius: 8,
              padding: '4px 10px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {tr('Close')}
          </button>
        </div>
      ) : null}
      <div
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          gap: 16,
          margin: "10px 0 14px",
          minHeight: 44,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifySelf: "start" }}>
          <h2 style={{ margin: 0 }}>{tr('BBQ Schedule')}</h2>
          <button
            style={historyLaunchButtonStyle}
            onClick={() => setHistoryOpen(true)}
            title={bbqTerm("Open served meats history")}
          >
            <span aria-hidden="true">{"\u{1F4DC}"}</span>
            {tr("History")}
          </button>
        </div>

        <div style={{ justifySelf: "center", pointerEvents: "none" }}>
          <b style={headerClockStyle}>
            <span aria-hidden="true">{"\u{1F551}"}</span>
            {currentTime.toTimeString().slice(0,5)}
          </b>
        </div>

        <div
          style={{
            position: "fixed",
            top: isTablet ? "25px" : "80px",
            right: "30px",
            display: "flex",
            gap: 8,
            zIndex: 9999,
          }}
        >
          <button
            style={{
              ...utilityButtonStyle,
              background: (alarmsMuted ? "darkGreen" : "darkRed"),
            }}
            onClick={toggle}
          >
            {tr("Alarms")}{" "}
            {alarmsMuted ? (
              <>ON <span aria-hidden="true">&#x266B;</span></>
            ) : (
              tr("Muted")
            )}
          </button>

          <button
            style={{
              ...utilityButtonStyle,
              background: (voiceOverEnabled ? "darkGreen" : "darkRed"),
            }}
            onClick={toggleVoiceOver}
          >
            {tr("VoiceOver")}{" "}
            {voiceOverEnabled ? (
              <>ON <span aria-hidden="true">&#x1F50A;</span></>
            ) : (
              <>OFF <span aria-hidden="true">&#x1F507;</span></>
            )}
          </button>
        </div>
      </div>
      {schedule.length === 0 && false ? (
        <p>{bbqTerm('No more assigned meats scheduled for today found yet...')}</p>
      ) : (
        <div className="table-wrap">
          <table className={isTablet ? 'bbqTable scale-tablet-95' : 'bbqTable'} style={{ textAlign: 'center', width: '100%' }}>
            <thead>
              <tr>
                <th>#</th>
                <th>{tr('Done')}</th> 
                <th>{tr('Pass')}</th>
                <th>{tr('Reservation')}</th>
                <th>{tr('Tables')}</th>
                <th>{tr('Client')}</th>
                <th>{tr('Order')}</th>
                <th>{tr('Arrival')}</th>
                <th>{tr('ID')}</th>
                <th>{bbqTerm('Meat')}</th>
                <th>{tr('Requested Qty')}</th>
                <th>{tr('Actual Qty')}</th>
                <th>{tr('Cook Type')}</th>
                <th>{tr('Minutes')}</th>
                <th>{tr('Time Left')}</th>
                <th>{tr('Start Time')}</th>
                <th>{tr('Ready Time')}</th>
                <th>{tr('Alone')}</th>
                <th>{tr('Siders')}</th>
                <th>{tr('Fillet')}</th>
                <th>{tr('BBQ')}</th>               
                <th>{tr('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {groups.order.map((resId, gIndex) => {

                const items = groups.map[resId];
                const open = !!expandedByRes[resId];
                const multi = items.length > 1;

                const toggleGroup = () => {
                  if (!multi) return; // no collapse for single item reservations
                  setExpandedByRes(prev => ({ ...prev, [resId]: !open }));
                };

                // ---- group aggregates (for collapsed summary) ----
                const toMin = (t) => (t ? parseInt(t.slice(0,2),10) * 60 + parseInt(t.slice(3,5),10) : null);
                const fromMin = m => {
                  return m == null ? '' : tr('{{var1}}:{{var2}}', {
                    var1: String(Math.floor(m/60)).padStart(2,'0'),
                    var2: String(m%60).padStart(2,'0')
                  });
                };

                const maxMinutes = Math.max(...items.map(i => Number(i.minutes) || 0));
                const minStart = items.reduce((acc,i)=> {
                  const v = toMin(i.start_time);
                  return (v==null) ? acc : (acc==null ? v : Math.min(acc, v));
                }, null);
                // Fallback: if no ready_time at all, show max start time in Ready column
                const maxStart = items.reduce((acc,i)=> {
                  const v = toMin(i.start_time);
                  return (v==null) ? acc : (acc==null ? v : Math.max(acc, v));
                }, null);

                const maxReady = items.reduce((acc, i) => {
                  const v = toMin(i.ready_time);
                  return (v == null) ? acc : (acc == null ? v : Math.max(acc, v));
                }, null);

                // Fallback to maxStart if none of the rows has a ready_time
                const readyForSummary = (maxReady != null ? maxReady : maxStart);

                const getTL = (it) => {
                  const v = timeleftData[it.assignedmeat];
                  return (v==null || typeof v !== 'number') ? Number.POSITIVE_INFINITY : v;
                };
                const minTLItem = items.reduce((best, it) => (getTL(it) < getTL(best) ? it : best), items[0]);
                const minTL = timeleftData[minTLItem.assignedmeat];

                return (
                  <React.Fragment key={`res-${resId}`}>
                    {items.map((item, index) => {
                      const isFirstRow = multi && index === 0;
                      const summaryMode = isFirstRow && !open;

                      const pendingConfirm = !item.confirmed;
                      // 11/2026: τραπέζια/πελάτης έρχονται με κάθε poll από το schedule (αλλαγή
                      // τραπεζιού φαίνεται αμέσως)· το details μένει μόνο ως εφεδρεία.
                      const tables = item.tables ?? details[item.reservation_id]?.tables ?? "";
                      const lastname = item.client_lastname ?? details[item.reservation_id]?.client?.lastname ?? "";
                      const firstname = item.client_firstname ?? details[item.reservation_id]?.client?.firstname ?? "";
                      const clientName = tr('{{lastname}} {{firstname}}', {
                        lastname,
                        firstname
                      }).trim();
                      const isLastVisibleRow = open ? index === items.length - 1 : index === 0;
                      const groupBorderColor = toSoftGroupBorderColor(colors[item.reservation_id]);

                      if (!open && multi && index > 0) {
                        return null;
                      }

                      // ---- Σημειώσεις: πελάτη / κράτησης / προπαραγγελίας / προτιμήσεις (11/2026) ----
                      // Της κράτησης: στην 1η ορατή γραμμή της ομάδας. Της γραμμής: στη γραμμή της
                      // (στη συμπτυγμένη ομάδα, όλες μαζί στη σύνοψη, με το όνομα του κρέατος).
                      const resLines = index === 0 ? [
                        { icon: '👤', label: tr('Client'), text: item.client_comment },
                        { icon: '📝', label: tr('Reservation'), text: item.reservation_comment },
                        { icon: '🍖', label: tr('Pre-order'), text: item.preorder_comment },
                      ] : [];
                      const orderPrefs = index === 0 ? parsePrefs(item.preorder_prefs) : [];
                      let lineLines = [];
                      let linePrefs = [];
                      if (summaryMode) {
                        lineLines = items
                          .map((it) => {
                            const parts = [...parsePrefs(it.prefs), String(it.note || '').trim()].filter(Boolean);
                            return parts.length ? { icon: '•', label: it.meat_name, text: parts.join(' · ') } : null;
                          })
                          .filter(Boolean);
                      } else {
                        linePrefs = parsePrefs(item.prefs);
                        if (String(item.note || '').trim()) lineLines = [{ icon: '🔸', label: tr('Note'), text: item.note }];
                      }
                      const bubbleLines = [...resLines, ...lineLines];
                      const bubblePrefs = [...new Set([...orderPrefs, ...linePrefs])];
                      const hasNotes = bubblePrefs.length > 0 || bubbleLines.some((l) => String(l.text || '').trim());
                      const notesKey = summaryMode ? `r${resId}` : `a${item.assignedmeat}`;
                      const notesClosed = !!notesCollapsed[notesKey];
                      const bubbleOpen = hasNotes && !notesClosed;

                      return (
                        <React.Fragment key={item.assignedmeat}>
                        <tr
                          key={item.assignedmeat}
                          className={pendingConfirm ? 'pending-confirm' : ''}
                          onClick={multi ? toggleGroup : undefined}               // ? click any row in the group
                          style={{
                            ...(index > 0 && multi ? rowAnimatedStyle(true) : {}),
                            height: 35,
                            opacity: pendingConfirm ? 0.55 : 1,
                            cursor: multi ? 'pointer' : 'default',
                            background: summaryMode ? 'rgba(0,0,0,0.03)' : 'transparent',
                            boxShadow: isLastVisibleRow && !bubbleOpen ? `inset 0 -1px 0 ${groupBorderColor}` : 'none'
                          }}
                          title={pendingConfirm ? 'Pending confirmation' : undefined}
                        >
                          {/* # with chevron on the first row only */}
                          <td style={{textAlign: 'right'}}>
                            {multi && (isFirstRow ? (
                              <span style={{ marginLeft: 6 }}>{open ? "\u25BE" : "\u25B8"}</span>
                            ) : (
                              <span style={{ marginLeft: 6 }}>{"\u2022"}</span>
                            ))}
                            {gIndex + 1}{open && multi ? tr('.{{var1}}', {
                            var1: index + 1
                          }) : ''}
                          </td>
                          {/* ? Done checkbox column */}
                          <td>
                            {(!multi || open) && !summaryMode && (
                              <input
                                type="checkbox"
                                checked={buttonStates[item.assignedmeat] === "done"}
                                disabled={sidersOf(item).sidersPending}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleDoneToggle(item.assignedmeat, e.target.checked);
                                }}
                              />
                            )}
                          </td>
                          {/* Pass checkbox column (stored on the assignedmeat row) */}
                          <td>
                            {(!multi || open) && !summaryMode && (
                              <input
                                type="checkbox"
                                checked={!!Number(item.pass)}
                                disabled={!!savingPassMap[String(item.assignedmeat)]}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  togglePass(item.assignedmeat, e.target.checked);
                                }}
                                title={tr('Pass')}
                              />
                            )}
                          </td>
                          {/* Reservation (no chevron here anymore) */}
                          <td style={{ userSelect: 'none' }}>
                            <span
                              title={item.confirmed ? 'Confirmed' : 'Not confirmed yet'}
                              style={{
                                color: item.confirmed ? 'darkGreen' : 'red',
                                fontWeight: 700,
                                marginRight: 6,
                                display: 'inline-block',
                                width: 16,
                                textAlign: 'center'
                              }}
                            >
                              {item.confirmed ? "\u2713" : "?"}
                            </span>
                            {Number(item.last_assignedstate) > 4 && (
                              <span
                                title={item.last_assignedstate_name || tr('last state #{{last_assignedstate}}', {
                                  last_assignedstate: item.last_assignedstate
                                })}
                                style={{
                                  display: 'inline-block', width: 15, height: 15, border: '1px solid black',
                                  borderRadius: '50%', background: item.last_assignedstate_color || '#999',
                                  margin: '-3px 7px 0 0', verticalAlign: 'middle'
                                }}
                              />
                            )}
                            {item.reservation_id}
                          </td>
                          {/* Tables, Client, Order, Arrival: always shown */}
                          <td>{tables}</td>
                          <td>
                            {clientName}
                            {hasNotes && notesClosed && (
                              <span style={{ marginLeft: 6 }}>
                                <NotesBubble lines={bubbleLines} prefs={bubblePrefs} collapsed onToggle={() => toggleNotes(notesKey)} />
                              </span>
                            )}
                          </td>
                          <td>{item.order || ''}</td>
                          <td>{item.reservation_arrival_time}</td>
                          {/* Meat + Quantity + Cook Type */}
                          {summaryMode ? (
                            <td colSpan={5}>
                              <i style={{fontSize: 'large'}}>{items.length}{' '}{tr('items')}</i>
                            </td>
                          ) : (
                            <>
                              <td>
                                {(!multi || open) && <b style={{ color: 'darkBlue' }}>{('  ' + item.assignedmeat).slice(-2)}</b>}
                              </td>   
                              <td>{item.meat_name}</td>
                              <td><i style={{ fontSize: 13 }}>{item.grams}</i></td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={getActualQtyInputValue(item)}
                                  disabled={!!savingActualQtyMap[String(item.assignedmeat)]}
                                  onChange={(e) => handleActualQtyDraftChange(item.assignedmeat, e.target.value)}
                                  onBlur={(e) => saveActualQuantity(item, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      saveActualQuantity(item, e.currentTarget.value);
                                    }
                                  }}
                                  style={{
                                    width: 58,
                                    textAlign: 'center',
                                    fontWeight: 700,
                                    borderRadius: 6,
                                    border: '1px solid #cbd5e1',
                                    padding: '2px 4px',
                                    background: savingActualQtyMap[String(item.assignedmeat)] ? '#f3f4f6' : 'white'
                                  }}
                                  title={tr('Edit actual quantity')}
                                />
                              </td>
                              <td>{item.cooktype_name}</td>
                            </>
                          )}
                          {/* Minutes */}
                          <td>
                            {summaryMode ? (
                              <span>
                                <i style={{ fontSize: 'x-small' }}>{tr('max:')}</i>{' '}{maxMinutes}'
                              </span>
                            ) : (
                              <span>{item.minutes}'</span>
                            )}
                          </td>
                          {/* Time Left */}
                          <td>
                            {summaryMode ? (
                              Number.isFinite(minTL) && (
                                <>
                                  <i style={{fontSize: 'x-small'}}>{tr('min:')}</i> {minTL}' 
                                  <div style={{ width: '100%', backgroundColor: '#e0e0e0', height: 9, marginTop: 1 }}>
                                    <div
                                      style={{
                                        width: tr('{{var1}}%', {
                                          var1: (minTL / (minTLItem.minutes || 1)) * 100
                                        }),
                                        backgroundColor: minTL > 0 ? '#4caf50' : '#f44336',
                                        height: 9
                                      }}
                                    />
                                  </div>
                                </>
                              )
                            ) : (
                              timeleftData[item.assignedmeat] != null && (
                                <>
                                  {timeleftData[item.assignedmeat]}' 
                                  <div style={{ width: '100%', backgroundColor: '#e0e0e0', height: 9, marginTop: 1 }}>
                                    <div
                                      style={{
                                        width: tr('{{var1}}%', {
                                          var1: (timeleftData[item.assignedmeat] / item.minutes) * 100
                                        }),
                                        backgroundColor: timeleftData[item.assignedmeat] > 0 ? '#4caf50' : '#f44336',
                                        height: 9
                                      }}
                                    />
                                  </div>
                                </>
                              )
                            )}
                          </td>
                          {/* Start Time */}
                          <td style={{
                            animation: (!buttonStates[item.assignedmeat] && calculateTimeDifference(item.start_time) <= 5
                              ? ((calculateTimeDifference(item.start_time) <= 0 ? 'blinkerDone' : 'blinkerPrepare') + ' 2s linear infinite')
                              : 'none')
                          }}>
                            {summaryMode ? (
                              <span>
                                <i style={{ fontSize: 'x-small' }}>{tr('min:')}</i>{' '}{fromMin(minStart)}
                              </span>
                            ) : (
                              <>
                                <span>{item.start_time?.slice(0, -3)}</span>
                                {calculateTimeDifference(item.start_time) > 0 && (
                                  <div style={{ width: '100%', backgroundColor: '#e0e0e0', height: 9, marginTop: 1, position: 'relative' }}>
                                    <div style={getBarStyle(calculateTimeDifference(item.start_time))}>
                                      {tr('{{var1}}\'', {
                                        var1: Math.ceil(Math.max(calculateTimeDifference(item.start_time), 0))
                                      })}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                          {/* Ready Time (max ready, fallback to max start) */}
                          <td
                            className={
                              (!summaryMode &&
                                timeleftData[item.assignedmeat] != null &&
                                Number(timeleftData[item.assignedmeat]) <= 1 &&
                                buttonStates[item.assignedmeat] !== "done")
                                ? "flashAlmostReady"
                                : ""
                            }
                          >
                            {summaryMode ? (
                              <span>
                                <i style={{ fontSize: 'x-small' }}>{tr('max:')}</i>{' '}{fromMin(readyForSummary)}
                              </span>
                            ) : (
                              item.ready_time?.slice(0, -3)
                            )}
                          </td>
                          {/* Alone (same as first row when collapsed) */}
                          <td style={{ display: 'inline-flex' }}>
                            {summaryMode ? (
                              <>
                                <div style={{ width: 15, height: 39, margin: '-3px 10px -3px 0', background: colors[item.reservation_id] }} />
                                {(item.standalone.data[0] ? "\u2713" : '')}
                              </>
                            ) : (
                              <>
                                <div style={{ width: 15, height: 39, margin: '-3px 10px -3px 0', background: colors[item.reservation_id] }} />
                                {(item.standalone.data[0] ? "\u2713" : '')}
                              </>
                            )}
                          </td>
                          {/* Siders */}
                          <td
                            onClick={(e) => {
                              e.stopPropagation(); // don't toggle the group row
                              // \u03a7\u03b5\u03b9\u03c1\u03bf\u03ba\u03af\u03bd\u03b7\u03c4\u03bf OnTheSide \u03c0\u03c1\u03bf\u03c2 \u03ba\u03bf\u03c5\u03b6\u03af\u03bd\u03b1 \u039c\u039f\u039d\u039f \u03b3\u03b9\u03b1 \u03c4\u03bf core flow \u2014
                              // \u03c4\u03b1 \u03c3\u03c5\u03bd\u03bf\u03b4\u03b5\u03c5\u03c4\u03b9\u03ba\u03ac \u03c4\u03b7\u03c2 \u03c0\u03b1\u03c1\u03b1\u03b3\u03b3\u03b5\u03bb\u03b9\u03bf\u03bb\u03b7\u03c8\u03af\u03b1\u03c2 \u03b5\u03c4\u03bf\u03b9\u03bc\u03ac\u03b6\u03bf\u03bd\u03c4\u03b1\u03b9 \u03b1\u03c0\u03cc \u03c4\u03bf \u03b4\u03b9\u03ba\u03cc
                              // \u03c4\u03bf\u03c5\u03c2 queue, \u03b4\u03b5\u03bd \u03ad\u03c7\u03b5\u03b9 \u03bd\u03cc\u03b7\u03bc\u03b1 \u03c4\u03bf force \u03b5\u03b4\u03ce.
                              const s = sidersOf(item);
                              if ((!multi || open) && item.ontheside.data[0] && s.sidersPending && s.orderingTotal === 0) {
                                handleForceOnTheSide(item.reservation_id);
                              }
                            }}
                            style={{
                              color: (!multi || open) ? (sidersOf(item).sidersReady ? 'green' : 'red') : undefined,
                              cursor:
                                (!multi || open) && item.ontheside.data[0] && sidersOf(item).sidersPending && sidersOf(item).orderingTotal === 0
                                  ? 'pointer'
                                  : 'default',
                            }}
                            title={
                              (!multi || open) && sidersOf(item).sidersPending
                                ? (sidersOf(item).orderingTotal > 0
                                    ? tr('Siders from the order: {{ready}}/{{total}} ready', {
                                        ready: Number(item.ordering_sides_ready || 0),
                                        total: Number(item.ordering_sides_total || 0),
                                      })
                                    : tr('Send OnTheSide action to kitchen for reservation {{reservation_id}}', {
                                        reservation_id: item.reservation_id,
                                      }))
                                : undefined
                            }
                          >
                            {(!multi || open) ? (sidersOf(item).hasSiders ? "\u2713" : '') : ''}
                          </td>
                          {/* Fillet */}
                          <td style={{ color: (!multi || open) ? (item.filleting?.data?.[0] ? 'green' : 'red') : undefined }}>
                            {(!multi || open) ? (item.filleting?.data?.[0] ? "\u2713" : '') : ''}
                          </td>
                          {/* BBQ */}
                          <td>
                            {summaryMode ? (
                              <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                <button
                                  title={tr('Print grouped stickers')}
                                  onClick={(e) => { e.stopPropagation(); handlePrintStickerGroup(items); }}
                                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '2px 4px' }}
                                >{"\u{1F5A8}\u{FE0F}"}</button>
                              </div>
                            ) : ((!multi || open) && (() => {
                              const canSelectMany = items.length > 1;
                              const printTone = getPrintStatusTone(item);
                              const printWrapStyle = getPrintControlWrapStyle(printTone, canSelectMany);
                              const isSelectedForPrint = !!selectedPrintMeats[String(item.assignedmeat)];

                              return (
                                <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                  <div
                                    style={printWrapStyle}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {canSelectMany && (
                                      <input
                                        type="checkbox"
                                        checked={isSelectedForPrint}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          togglePrintSelection(item.assignedmeat, e.target.checked);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        title={bbqTerm('Select meat for printing')}
                                        style={{
                                          margin: 0,
                                          width: 13,
                                          height: 13,
                                          transform: 'scale(0.82)',
                                          transformOrigin: 'center',
                                          accentColor: PRINT_TONE_COLORS[printTone]?.fg,
                                          cursor: 'pointer'
                                        }}
                                      />
                                    )}

                                    <button
                                      title={getRowPrinterTitle(item)}
                                      onClick={(e) => { e.stopPropagation(); handlePrintSticker(item); }}
                                      style={getPrintButtonStyle(printTone)}
                                    >{"\u{1F5A8}\u{FE0F}"}</button>
                                  </div>
                                {bbqs.length > 0 && (
                                  <select
                                    value={item.bbq || ""}
                                    onChange={(e) => { e.stopPropagation(); handleBBQChange(item.assignedmeat, e.target.value); }}
                                  >
                                    <option value="">{tr('None')}</option>
                                    {bbqs.map((bbq) => (
                                      <option key={bbq.id} value={bbq.id}>{bbq.name}</option>
                                    ))}
                                  </select>
                                )}
                                </div>
                              );
                            })())}
                          </td>
                          {/* Actions */}
                          <td style={{ textAlign: 'left' }}>
                            {(!multi || open) && (
                              <>
                                {!buttonStates[item.assignedmeat] && (
                                  <button title={tr('Start')} onClick={(e) => { e.stopPropagation(); handleStart(item.assignedmeat, item.minutes); }}>{tr('Start')} <span aria-hidden="true" style={{ animation: (calculateTimeDifference(item.start_time) < 5 ? 'blinkerStart 2s linear infinite' : 'none') }}>{"\u23F5\uFE0E"}</span>
                                  </button>
                                )}
                                {(buttonStates[item.assignedmeat] === "start" || buttonStates[item.assignedmeat] === "restart" || buttonStates[item.assignedmeat] === "stop" || buttonStates[item.assignedmeat] === "run" || buttonStates[item.assignedmeat] === "done") && (
                                  <>
                                    <button title={tr('Restart')} onClick={(e) => { e.stopPropagation(); handleRestart(item.assignedmeat, item.minutes); }}><><span aria-hidden="true">{"\u23EE"}</span>{" "}{tr('Restart')}</></button>
                                    <>
                                      <button title={tr('+1\'')} onClick={(e) => { e.stopPropagation(); handleRetime(item.assignedmeat, 1); }}><>{tr("+1'")} {"\u23EA"}</></button>
                                      <>
                                        <button
                                          title={tr('Start/Stop')}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggle(item.assignedmeat, buttonStates[item.assignedmeat] === "stop" ? 'run' : 'stop');
                                          }}
                                        >
                                          <span style={{ color: buttonStates[item.assignedmeat] === "stop" ? 'red' : 'green' }}>
                                            {buttonStates[item.assignedmeat] === "stop" ? <>Continue {"\u23EF"}</> : <>Pause {"\u23F8"}</>}
                                          </span>
                                        </button>
                                        <button title={tr('-1\'')} onClick={(e) => { e.stopPropagation(); handleRetime(item.assignedmeat, -1); }}><>{"\u23E9"} {tr("-1'")}</></button>
                                        <button
                                          title={tr('Done')}
                                          disabled={sidersOf(item).sidersPending}
                                          onClick={(e) => { e.stopPropagation(); handleDone(item.assignedmeat); }}
                                        ><>{tr('Done')} {"\u23ED"}</></button>
                                      </>
                                    </>
                                    {buttonStates[item.assignedmeat] === "done" && !sidersOf(item).sidersPending && (
                                      <button title={tr('Served')} onClick={(e) => { e.stopPropagation(); handleServed(item.assignedmeat); }}><>{"\u23FA"} {tr('Served')}</></button>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                        {bubbleOpen && (
                          <tr style={{ boxShadow: isLastVisibleRow ? `inset 0 -1px 0 ${groupBorderColor}` : 'none' }}>
                            <td colSpan={22} style={{ textAlign: 'left', padding: '0 10px 6px 70px', borderTop: 'none' }}>
                              <NotesBubble lines={bubbleLines} prefs={bubblePrefs} collapsed={false} onToggle={() => toggleNotes(notesKey)} />
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          
          </table>
        </div>
      )}
      {historyOpen && (
        <div
          onClick={() => setHistoryOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.58)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: isTablet ? 12 : 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(96vw, 1500px)",
              maxHeight: "90vh",
              background: "white",
              borderRadius: 14,
              boxShadow: "0 18px 45px rgba(0,0,0,0.28)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "14px 18px 10px",
                borderBottom: "1px solid #d9d9d9",
                background: "linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#103b6f" }}>
                    {bbqTerm("Served meats history")}
                  </div>
                  <div style={{ fontSize: 13, color: "#4f6276", marginTop: 4 }}>
                    {bbqTerm("Review served meats by day and search by reservation, client, meat, table, BBQ or order.")}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap" }}>
                  <div style={{ ...historyFilterCardStyle, minWidth: 190, padding: "8px 10px" }}>
                    <div style={historyFieldLabelStyle}>{tr("Date")}</div>
                    <input
                      type="date"
                      value={historyDate}
                      onChange={(e) => setHistoryDate(e.target.value)}
                      style={{ ...historyInputStyle, paddingRight: 10 }}
                    />
                  </div>

                  <button style={historyPrimaryButtonStyle} onClick={() => fetchHistory(historyDate)}>
                    {historyLoading ? tr("Loading...") : tr("Refresh")}
                  </button>

                  <button style={historySecondaryButtonStyle} onClick={resetHistoryFilters}>
                    {tr("Clear filters")}
                  </button>

                  <button
                    onClick={() => setHistoryOpen(false)}
                    style={historyCloseButtonStyle}
                  >
                    {tr("Close")}
                  </button>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isTablet ? "1fr" : "1.2fr 1fr 1fr 1fr 1fr",
                  gap: 10,
                  marginTop: 14,
                }}
              >
                <div style={historyFilterCardStyle}>
                  <div style={historyFieldLabelStyle}>{tr("General")}</div>
                  <input
                    type="text"
                    style={historyInputStyle}
                    placeholder={bbqTerm("Reservation, client, meat, BBQ...")}
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                  />
                </div>
                <div style={historyFilterCardStyle}>
                  <div style={historyFieldLabelStyle}>{tr("Reservation / Tables")}</div>
                  <input
                    type="text"
                    style={historyInputStyle}
                    placeholder={tr("Reservation ID or tables")}
                    value={historyReservationSearch}
                    onChange={(e) => setHistoryReservationSearch(e.target.value)}
                  />
                </div>
                <div style={historyFilterCardStyle}>
                  <div style={historyFieldLabelStyle}>{tr("Client")}</div>
                  <input
                    type="text"
                    style={historyInputStyle}
                    placeholder={tr("Client name")}
                    value={historyClientSearch}
                    onChange={(e) => setHistoryClientSearch(e.target.value)}
                  />
                </div>
                <div style={historyFilterCardStyle}>
                  <div style={historyFieldLabelStyle}>{bbqTerm("Meat / Cook type / ID")}</div>
                  <input
                    type="text"
                    style={historyInputStyle}
                    placeholder={bbqTerm("Meat, cook type or ID")}
                    value={historyMeatSearch}
                    onChange={(e) => setHistoryMeatSearch(e.target.value)}
                  />
                </div>
                <div style={historyFilterCardStyle}>
                  <div style={historyFieldLabelStyle}>{tr("BBQ / Order")}</div>
                  <input
                    type="text"
                    style={historyInputStyle}
                    placeholder={tr("BBQ or order")}
                    value={historyBbqSearch}
                    onChange={(e) => setHistoryBbqSearch(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ marginTop: 10, fontSize: 13, color: "#3f5266" }}>
                {historyLoading
                  ? tr("Loading history...")
                  : tr("Results: {{count}}", { count: filteredHistoryRows.length })}
              </div>

              {historyError && (
                <div style={{ marginTop: 8, color: "#b42318", fontWeight: 600 }}>
                  {historyError}
                </div>
              )}
            </div>

            <div style={{ padding: 14, overflow: "auto" }}>
              <table
                className={isTablet ? 'bbqTable scale-tablet-95' : 'bbqTable'}
                style={{ textAlign: "center", width: "100%", minWidth: 900 }}
              >
                <thead>
                  <tr>
                    <th>{tr("Served")}</th>
                    <th>{tr("Reservation")}</th>
                    <th>{tr("Tables")}</th>
                    <th>{tr("Client")}</th>
                    <th>{tr("Order")}</th>
                    <th>{tr("Arrival")}</th>
                    <th>{tr("ID")}</th>
                    <th>{bbqTerm("Meat")}</th>
                    <th>{tr("Requested Qty")}</th>
                    <th>{tr("Actual Qty")}</th>
                    <th>{tr("Cook Type")}</th>
                  </tr>
                </thead>
                <tbody>
                  {!historyLoading && filteredHistoryRows.length === 0 && (
                    <tr>
                      <td colSpan={11} style={{ padding: 20 }}>
                        {bbqTerm("No served meats found for the selected filters.")}
                      </td>
                    </tr>
                  )}

                  {filteredHistoryRows.map((row, index) => {
                    const clientName = buildHistoryClientName(row);
                    const servedAt = formatHistoryTime(
                      getFirstDefinedValue(row, ["served_at", "served_time", "updated_at", "created_at"])
                    );
                    const arrivalTime = formatHistoryTime(
                      getFirstDefinedValue(row, ["arrival", "reservation_arrival_time"])
                    );

                    return (
                      <tr key={`${getFirstDefinedValue(row, ["assignedmeat", "id"]) || "history"}-${index}`}>
                        <td>{servedAt}</td>
                        <td>{getFirstDefinedValue(row, ["reservation_id", "reservation.id"])}</td>
                        <td>{getFirstDefinedValue(row, ["tables", "reservation_tables", "reservation.tables"])}</td>
                        <td>{clientName}</td>
                        <td>{getFirstDefinedValue(row, ["order"])}</td>
                        <td>{arrivalTime}</td>
                        <td><b style={{ color: "darkBlue" }}>{getFirstDefinedValue(row, ["assignedmeat", "id"])}</b></td>
                        <td>{getFirstDefinedValue(row, ["meat_name", "meat.name", "meat"])}</td>
                        <td>{getFirstDefinedValue(row, ["grams", "quantity"])}</td>
                        <td>{getFirstDefinedValue(row, ["actual_quantity", "actualQuantity", "grams", "quantity"])}</td>
                        <td>{getFirstDefinedValue(row, ["cooktype_name", "cooktype.name", "cooktype"])}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BBQ;